import ReplayIcon from "@mui/icons-material/Replay";
import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Pagination,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { ContactRequestButton } from "components/commons/ContactRequestButton";
import { DiscardProgressDialog } from "components/dialog/DiscardProgressDialog";
import type { ColorFilterValue } from "components/filter/color-filter/ColorFilter";
import { SearchFilter } from "components/filter/SearchFilter";
import { FilterChips } from "components/filter-chips/FilterChips";
import { PAGE_SIZE_OPTIONS } from "constants/pagination";
import { useAuthUser } from "hooks/authentication";
import {
  useColoredMaterials,
  useReferenceMaterials,
} from "hooks/colored-materials";
import type { FilterEntry } from "hooks/filters";
import type { SortEntry } from "hooks/sort";
import type { CdcUserAttributes } from "models/cdcUser";
import type { ChangeEvent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getOpacityValue } from "service/autocomplete-lookup";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { useExploreImage } from "../ExploreImageContext";
import { MatchedMaterialsPanel } from "./MatchedMaterialsPanel";
import { RegionTabs } from "./RegionTabs";
import { ResultsProvider, useResults } from "./ResultsContext";
import { SearchCriteriaPanel } from "./sidebar/SearchCriteriaPanel";

const ResultsViewContent = () => {
  const { state, goBack } = useExploreImage();
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const navigate = useNavigate();
  const cdcUser: CdcUserAttributes | undefined =
    getDataFromLocalstorageByKey("cdcUser");
  const isLogin = Boolean(user?.email) || Boolean(cdcUser);
  const {
    activeRegionIndex,
    setActiveRegionIndex,
    selectedColorIndex,
    setSelectedColorIndex,
    clearSelectedColor,
    searchQuery,
    setSearchQuery,
  } = useResults();
  const [sort, setSort] = useState<SortEntry[]>([
    { property: "deltaE", direction: "asc" },
  ]);
  const [extraFilters, setExtraFilters] = useState<FilterEntry[]>([]);

  const [page, setPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [goToPage, setGoToPage] = useState("");

  // "Current Progress Will Be Lost" gating: removing the Reference Image
  // chip, removing the Color:CIE-Lab chip, and hitting "Reset All" all
  // discard in place, staying on the results view — the reference-search API
  // works fine with `cie_lab` and/or `filter_cie_lab` absent (see
  // docs/reference-search-api.md), so none of these need to navigate away.
  // Changing the Color System dropdown is the only trigger that still routes
  // to /search (AC#6/7), since it's picking a different system entirely.
  const [pendingDiscard, setPendingDiscard] = useState(false);
  const [pendingResetAll, setPendingResetAll] = useState(false);
  const [pendingColorDelete, setPendingColorDelete] = useState(false);
  const [pendingColorSystem, setPendingColorSystem] = useState<string | null>(
    null,
  );
  const [searchFilterResetKey, setSearchFilterResetKey] = useState(0);

  const activeExtractResult = state.extractResults?.regions[activeRegionIndex];
  const activePalette = activeExtractResult?.palette ?? [];
  const detectedEffects = activeExtractResult?.detectedEffects;
  const activeRegion = state.regions[activeRegionIndex];
  const selectedColor = activePalette[selectedColorIndex] ?? null;

  // The extracted image color is the primary reference (`cie_lab`); the user's
  // own color/system selections live in `extraFilters` and are intersected with
  // it by the reference-search endpoint (see referenceSearchToParams).
  const referenceCieLab = selectedColor?.cieLab;

  // Palette swatch selection is a helper that seeds the CIE-Lab filter, which
  // becomes the filter in charge from that point on (CX-2908): auto-select
  // Color System = CIE-Lab (L*/a*/b* from the swatch) + Opacity = Opaque, so
  // the "Color: CIE-Lab" / "Opacity: Opaque" chips appear. Writes directly to
  // `extraFilters` (bypassing SearchFilter's onChange) so it never trips the
  // colorSystem-change interception below — that's for genuine user edits to
  // the dropdown only. Does not clear color/opacity on deselect; clearing is
  // the explicit discard flows below.
  useEffect(() => {
    if (!selectedColor) return;

    const colorValue: ColorFilterValue = {
      colorSystem: "cie_lab",
      color: {
        value: selectedColor.cieLab,
        name: t("color_system_cie_lab"),
        measurementCondition: t("src_filter_block_color_measurement"),
      },
    };
    const opaque = getOpacityValue("OPAQUE");

    setExtraFilters((prev) => [
      ...prev.filter((f) => f.type !== "color" && f.type !== "opacity"),
      { type: "color", value: colorValue },
      { type: "opacity", value: opaque ? [opaque] : [] },
    ]);
    // t deliberately excluded: it only builds translated strings here, and
    // including it would re-run this effect (clobbering hand-edited Lab
    // values) whenever the UI language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColor]);

  // Genuine user edits from the SearchFilter panel (dropdown, Lab textfields,
  // Opacity/Material/etc.) come through here. A colorSystem identity change
  // (the user picking a different option from the dropdown) is held pending
  // behind the discard-confirm dialog instead of applied immediately (AC#5) —
  // in-place edits within the same system (e.g. typing new Lab digits) pass
  // straight through. Only gated while the upload flow is actually active
  // (a reference image/palette is selected) — once discarded there's no
  // in-progress upload left to protect, so color-system changes apply
  // immediately like on the normal /search page.
  const handleFiltersChange = (newFilters: FilterEntry[]) => {
    if (!selectedColor) {
      setExtraFilters(newFilters);
      return;
    }

    const prevColorSystem = (
      extraFilters.find((f) => f.type === "color") as
        | FilterEntry<ColorFilterValue>
        | undefined
    )?.value?.colorSystem;
    const nextColor = newFilters.find((f) => f.type === "color") as
      | FilterEntry<ColorFilterValue>
      | undefined;

    if (
      nextColor?.value?.colorSystem &&
      nextColor.value.colorSystem !== prevColorSystem
    ) {
      setPendingColorSystem(nextColor.value.colorSystem);
      return;
    }

    setExtraFilters(newFilters);
  };

  // Reference Image chip removal (AC#2/4, revised per PO): unlike a full flow
  // discard(), this stays on the results view — only the reference
  // image/palette selection resets, so the Upload & Match accordion falls
  // back to its compact drag-drop uploader in place (matches the Figma
  // "Upload & Match" reset state, node 879:23836) rather than returning to
  // the top-level category tabs. The auto-selected Opacity filter is cleared
  // along with it — it only existed to mirror the (now-gone) reference
  // image's extracted swatch. The Color (CIE-Lab) filter is intentionally
  // kept, since the PO wants it to keep constraining the search on its own.
  const requestDiscard = () => setPendingDiscard(true);

  const confirmDiscard = () => {
    clearSelectedColor();
    setExtraFilters((prev) => prev.filter((f) => f.type !== "opacity"));
    setPendingDiscard(false);
  };

  // Color:CIE-Lab chip removal: gated behind the same discard-confirm dialog
  // as the reference image, since it's driven by the same extracted palette.
  // Per PO (revised): removing the CIE-Lab filter also discards the
  // reference image/palette selection — CIE-Lab without an image doesn't
  // make sense as a standalone filter, so both go together. Confirming
  // clears the reference image (falling back to the compact uploader, same
  // as confirmDiscard) plus the color/opacity filters.
  const requestColorFilterDelete = () => setPendingColorDelete(true);

  const confirmColorFilterDelete = () => {
    clearSelectedColor();
    setExtraFilters((prev) =>
      prev.filter((f) => f.type !== "color" && f.type !== "opacity"),
    );
    setPendingColorDelete(false);
  };

  // Color System dropdown change while the upload flow is active (AC#6/7).
  const confirmColorSystemChange = () => {
    if (!pendingColorSystem) return;
    const filters: FilterEntry[] = [
      { type: "color", value: { colorSystem: pendingColorSystem } },
    ];
    setPendingColorSystem(null);
    navigate("/search", { state: { filters } });
  };

  const cancelColorSystemChange = () => {
    setPendingColorSystem(null);
    // Force SearchFilter to remount so ColorFilter's internal selectedColorSystem
    // re-seeds from the unchanged extraFilters, snapping the dropdown back.
    setSearchFilterResetKey((k) => k + 1);
  };

  // Reset All (sidebar button and the results panel's "no results" action):
  // gated behind the same discard-confirm dialog as the reference image
  // chip, since it discards the same in-progress upload flow. Per PO
  // (revised): Reset All clears the reference image/palette (falling back
  // to the compact uploader, same as confirmDiscard) and every extra filter
  // *except* Color:CIE-Lab, plus the "search within results" query —
  // CIE-Lab is the one filter that survives every discard flow on this view.
  // Only prompt when there is genuinely something to discard (AC#1): a
  // reference image, a "search within results" query, or any applied filter
  // other than the Color:CIE-Lab entry that Reset All keeps anyway. Without
  // this guard the confirm dialog pops up even on a pristine view where
  // Reset All would be a no-op.
  const hasResettableState =
    selectedColor !== null ||
    searchQuery.trim() !== "" ||
    extraFilters.some(
      (f) =>
        f.type !== "color" ||
        (f as FilterEntry<ColorFilterValue>).value.colorSystem !== "cie_lab",
    );

  const requestResetAll = () => {
    if (!hasResettableState) return;
    setPendingResetAll(true);
  };

  const confirmResetAll = () => {
    clearSelectedColor();
    // Keep only the Color:CIE-Lab entry — other color-system selections
    // (e.g. Preliminary Color Code) also carry `type: "color"`, so filtering
    // on `type` alone left them behind (AC#2). Distinguish by colorSystem.
    setExtraFilters((prev) =>
      prev.filter(
        (f) =>
          f.type === "color" &&
          (f as FilterEntry<ColorFilterValue>).value.colorSystem === "cie_lab",
      ),
    );
    setSearchQuery("");
    setPendingResetAll(false);
  };

  // Reset to page 1 whenever the color or extra filters change
  useEffect(() => {
    setPage(1);
  }, [selectedColor, extraFilters]);

  // Keep querying as long as there's still something to search with — either
  // the reference image (selectedColor) or a standalone Color:CIE-Lab filter
  // left behind after discarding the reference image (confirmDiscard). Gating
  // on selectedColor alone would freeze the results on the last successful
  // fetch once the image is gone, even though extraFilters may still carry a
  // CIE-Lab filter the reference-search endpoint can run with on its own.
  // Requires an actual Lab value (not just a "color" filter entry): picking a
  // Color System option adds the entry immediately via onSelectedColorSystemChange
  // (ColorFilter.tsx), before any L*/a*/b* digits exist — without this check
  // that fires the reference search with neither cie_lab nor filter_cie_lab
  // set, which the endpoint rejects with 400.
  const hasColorFilter = extraFilters.some(
    (f) =>
      f.type === "color" &&
      (f as FilterEntry<ColorFilterValue>).value.color !== undefined,
  );

  // Once neither the reference image nor a Color:CIE-Lab filter survives
  // (color-chip delete, or Reset All + a subsequent color-chip delete), the
  // reference-search endpoint has nothing to intersect against and would
  // 400. Fall back to the plain color-search endpoint instead, which runs
  // fine with whatever filters remain — or with none, returning all items.
  const hasReference = selectedColor !== null || hasColorFilter;

  const [referenceMaterials, referenceError, isReferenceLoading] =
    useReferenceMaterials(
      referenceCieLab,
      extraFilters,
      sort,
      page - 1,
      itemPerPage,
      hasReference,
      state.initialSearchResult ?? undefined,
      state.initialSearchKey ?? undefined,
    );
  const [plainMaterials, plainError, isPlainLoading] = useColoredMaterials(
    extraFilters,
    sort,
    page - 1,
    itemPerPage,
    !hasReference,
  );
  const [coloredMaterials, error, isLoading] = hasReference
    ? [referenceMaterials, referenceError, isReferenceLoading]
    : [plainMaterials, plainError, isPlainLoading];

  const pageCount = Math.ceil(
    (coloredMaterials?.totalCount ?? 0) / itemPerPage,
  );

  const changePage = (_: unknown, p: number) => {
    setPage(p);
  };

  const handlePagesizeChange = (e: SelectChangeEvent) => {
    setItemPerPage(Number(e.target.value));
    setPage(1);
  };

  const handleGoToPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (
      event.target.value !== "" &&
      event.target.value &&
      Number(event.target.value) <= pageCount
    ) {
      setGoToPage(event.target.value);
    } else {
      setGoToPage("");
    }
  };

  const handleGoToPageKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (
      event.key === "Enter" &&
      goToPage !== "" &&
      Number(goToPage) &&
      Number(goToPage) <= pageCount
    ) {
      changePage(null, Number(goToPage));
    } else {
      setGoToPage("");
    }
  };

  const handleGoToPageBlur = () => {
    if (goToPage !== "" && Number(goToPage) && Number(goToPage) <= pageCount) {
      changePage(null, Number(goToPage));
    } else {
      setGoToPage("");
    }
  };

  if (!state.extractResults) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        padding: "24px",
        backgroundColor: "#F5F5F5",
      }}
    >
      {/* Top bar: filter chips + region tabs */}
      <Box>
        <Box>
          <FilterChips
            isFromResultsViewPage
            hasReferenceColor={selectedColor !== null}
            onReferenceColorDelete={requestDiscard}
            onColorFilterDelete={requestColorFilterDelete}
            filters={extraFilters}
            onChange={handleFiltersChange}
            t={t}
          />
        </Box>
        {selectedColor !== null && (
          <Box>
            <RegionTabs
              regions={state.regions}
              activeIndex={activeRegionIndex}
              onRegionChange={setActiveRegionIndex}
              imageSrc={state.uploadedImage}
            />
          </Box>
        )}
      </Box>

      {/* Two-column layout: sidebar + results */}
      <Grid container columnSpacing={4} rowSpacing={3} alignItems="flex-start">
        {/* Left sidebar */}
        <Grid
          size={{ xs: 12, lg: 4, xxl: 3 }}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <Toolbar disableGutters>
            <Typography
              component="span"
              fontWeight={500}
              marginRight={1}
              fontSize="var(--title-t2-18-regular-size, 18px)"
            >
              {t("src_search_criteria")}
            </Typography>
            <Button
              sx={{
                height: 16,
                fontSize: "var(--body-b2-14-regular-size, 14px)",
                marginLeft: "auto",
              }}
              onClick={requestResetAll}
              size="medium"
            >
              <ReplayIcon
                fontSize="small"
                sx={{ marginRight: "2px", marginBottom: "2px" }}
              />
              {t("src_reset_all")}
            </Button>
          </Toolbar>
          <SearchCriteriaPanel
            hasReferenceImage={selectedColor !== null}
            imageSrc={state.uploadedImage ?? ""}
            palette={activePalette}
            selectedColorIndex={selectedColorIndex}
            onColorSelect={setSelectedColorIndex}
            detectedEffects={detectedEffects}
            onChangeClick={goBack}
            activeRegion={activeRegion}
            regions={state.regions}
            onRegionClick={(regionId) => {
              const index = state.regions.findIndex((r) => r.id === regionId);
              if (index >= 0) setActiveRegionIndex(index);
            }}
          />
          <SearchFilter
            key={searchFilterResetKey}
            value={extraFilters}
            onChange={handleFiltersChange}
            columns={1}
            childColumns={3}
            canMinimize={true}
            defaultExpanded={true}
          />
        </Grid>

        {/* Right results area */}
        <Grid
          size={{ xs: 12, lg: 8, xxl: 9 }}
          sx={{
            minWidth: 0,
          }}
        >
          <MatchedMaterialsPanel
            coloredMaterials={coloredMaterials}
            isLoading={isLoading}
            error={error}
            sort={sort}
            onSortChange={setSort}
            onFilterReset={requestResetAll}
            regionLabel={activeRegion?.label}
            regionColor={activeRegion?.color}
          />

          {/* Pagination controls */}
          {!isLoading && pageCount > 1 && (
            <Stack alignItems="center" direction="row" mt={2}>
              <Pagination
                color="primary"
                shape="rounded"
                count={pageCount}
                page={page}
                onChange={changePage}
              />
              <FormControl
                sx={{ m: 1, minWidth: 120, display: "inline-block" }}
                size="small"
              >
                <Select
                  sx={{
                    float: "left",
                    backgroundColor: "#fff",
                    "> .MuiInputBase-root": { borderRadius: "2px !important" },
                  }}
                  id="ResultsItemPerPage"
                  value={`${itemPerPage}`}
                  onChange={handlePagesizeChange}
                >
                  {PAGE_SIZE_OPTIONS.map((size, index) => (
                    <MenuItem
                      key={`ResultsItemPerPage${index}`}
                      color="#1A1A1A"
                      value={size}
                    >
                      {size}/{t("src_pagination_page")}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  sx={{
                    float: "left",
                    height: "40px",
                    paddingTop: "10px",
                    marginLeft: 1,
                  }}
                  color="#1A1A1A"
                  component="div"
                >
                  {t("src_pagination_goto")}
                </Typography>
                <TextField
                  sx={{
                    backgroundColor: "#fff",
                    width: "60px",
                    marginLeft: 1,
                    "> .MuiInputBase-root": { borderRadius: "2px" },
                    color: "#1A1A1A",
                  }}
                  id="ResultsGotoPage"
                  variant="outlined"
                  onChange={handleGoToPageChange}
                  onBlur={handleGoToPageBlur}
                  onKeyDown={handleGoToPageKeyDown}
                  size="small"
                />
              </FormControl>
            </Stack>
          )}
        </Grid>
      </Grid>

      {/* Contact expert banner — shown to logged-in users only */}
      {isLogin && (
        <Grid
          size={12}
          sx={{
            backgroundColor: "#fff",
            textAlign: "center",
            justifyItems: "center",
            height: "144px",
            padding: "30px 0px",
            marginTop: "21px",
          }}
        >
          <ContactRequestButton t={t} products={coloredMaterials?.results} />
        </Grid>
      )}

      {/* AC#2-4: Reference Image chip removal */}
      <DiscardProgressDialog
        open={pendingDiscard}
        onCancel={() => setPendingDiscard(false)}
        onConfirm={confirmDiscard}
      />

      {/* Reset All (sidebar button + results panel's "no results" action) */}
      <DiscardProgressDialog
        open={pendingResetAll}
        onCancel={() => setPendingResetAll(false)}
        onConfirm={confirmResetAll}
      />

      {/* Color:CIE-Lab chip removal — drops the color/opacity filters in place */}
      <DiscardProgressDialog
        open={pendingColorDelete}
        onCancel={() => setPendingColorDelete(false)}
        onConfirm={confirmColorFilterDelete}
      />

      {/* AC#5-7: Color System dropdown change while the upload flow is active */}
      <DiscardProgressDialog
        open={pendingColorSystem !== null}
        onCancel={cancelColorSystemChange}
        onConfirm={confirmColorSystemChange}
      />
    </Box>
  );
};

export const ResultsView = () => {
  return (
    <ResultsProvider>
      <ResultsViewContent />
    </ResultsProvider>
  );
};
